import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface Notification {
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface NotificationContextType {
  showNotification: (notification: Notification) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState<Notification>({
    message: '',
    severity: 'info',
    duration: 6000,
  });

  const showNotification = useCallback((notif: Notification) => {
    setNotification({
      ...notif,
      duration: notif.duration ?? 6000,
    });
    setOpen(true);
  }, []);

  const showError = useCallback(
    (message: string) => {
      showNotification({ message, severity: 'error' });
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string) => {
      showNotification({ message, severity: 'warning' });
    },
    [showNotification]
  );

  const showSuccess = useCallback(
    (message: string) => {
      showNotification({ message, severity: 'success' });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string) => {
      showNotification({ message, severity: 'info' });
    },
    [showNotification]
  );

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <NotificationContext.Provider
      value={{ showNotification, showError, showWarning, showSuccess, showInfo }}
    >
      {children}
      <Snackbar
        open={open}
        autoHideDuration={notification.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
